/**
 * Global QueryClient — single source of truth for server-state caching.
 *
 * Strategy: stale-while-revalidate (TanStack Query's native model).
 * A component always renders cached data instantly (even hydrated from
 * IndexedDB after a cold start / offline launch) while a background
 * refetch runs whenever the data is older than `staleTime`.
 *
 * Global error handling lives HERE (QueryCache/MutationCache callbacks)
 * so individual hooks never need their own try/catch for reporting.
 * See ./errorBus.js for how errors are surfaced.
 */
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { QUERY_STALE_TIME_MS, QUERY_GC_TIME_MS } from './config';
import { reportQueryError } from './errorBus';

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // meta.silent === true lets a caller opt out of global reporting
    // (e.g. optional polling probes where failure is expected).
    onError: (error, query) => {
      if (query?.meta?.silent) return;
      reportQueryError(error, { scope: 'query', queryKey: query?.queryKey });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation?.meta?.silent) return;
      reportQueryError(error, { scope: 'mutation', mutationKey: mutation?.options?.mutationKey });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,      // 5 min (env-overridable)
      gcTime: QUERY_GC_TIME_MS,            // 24 h  (v5 name for cacheTime)
      // stale-while-revalidate: render cache immediately, refetch in
      // background when stale.
      refetchOnMount: true,
      refetchOnReconnect: true,            // critical for offline-first
      refetchOnWindowFocus: false,         // preserves existing app behaviour
      retry: 1,                            // one transparent retry on network flake
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    },
    mutations: {
      retry: 0,                            // mutations are never auto-retried
    },
  },
});
